import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Users, ArrowRight, Check, Coins, HeartPulse, GraduationCap } from "lucide-react";
import { EVENT, IMAGES, DAY_INCLUDES, SPONSOR_TIERS, TRIBUTE_TIER, LOGO, SPONSOR_DETAILS_VISIBLE, SPONSOR_DEADLINES } from "@/data/content";
import { SectionLabel } from "@/components/Section";
import { FoundationLink } from "@/components/FoundationLink";
import { SpotsBadge } from "@/components/SpotsBadge";
import { useSponsorConfig, remainingFor } from "@/hooks/useSponsorConfig";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

export default function Home() {
  const cfg = useSponsorConfig();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-espresso text-cream overflow-hidden">
        <img src={IMAGES.heroGolf} alt="Golf course at dawn" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/80 to-espresso/40" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24 md:pt-24 md:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <SectionLabel>{EVENT.edition} · {EVENT.location}</SectionLabel>
              <motion.h1
                initial="hidden" animate="show" variants={fadeUp}
                className="mt-6 uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.98]"
                style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: "0.01em" }}
              >
                The Heroes & Legacy <br /><span className="text-rust">Golf Classic</span>
              </motion.h1>
              <motion.p
                initial="hidden" animate="show" custom={1} variants={fadeUp}
                className="mt-6 max-w-xl text-base md:text-lg text-cream/85 leading-relaxed"
              >
                One day on the course. A lasting legacy of service. Join us for a four-person scramble that unites our community in support of the <span className="text-cream font-semibold">Wayne Walker Foundation</span> and the first responders who answer the call.
              </motion.p>
              <motion.div
                initial="hidden" animate="show" custom={2} variants={fadeUp}
                className="mt-9 flex flex-wrap gap-4"
              >
                <Link to="/register" data-testid="hero-register-btn" className="label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-cream/20 transition-transform hover:-translate-y-1 hard-shadow flex items-center gap-2">
                  Register a Team <ArrowRight size={16} />
                </Link>
                <Link to="/sponsor" data-testid="hero-sponsor-btn" className="label-font text-sm bg-transparent text-cream px-8 py-4 border border-cream/40 transition-colors hover:bg-cream/10">
                  Become a Sponsor
                </Link>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden lg:flex lg:col-span-4 justify-center">
              <img src={LOGO} alt="The Wayne Walker Heroes & Legacy Golf Classic" className="w-72 h-72 object-contain drop-shadow-2xl" />
            </motion.div>
          </div>
        </div>

        {/* WHEN / WHERE / WHO strip */}
        <div className="relative border-t border-cream/15 bg-espresso/60 backdrop-blur">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-cream/15">
            {[
              { icon: Calendar, label: "When", main: EVENT.dateLabel, sub: EVENT.dayLabel },
              { icon: MapPin, label: "Where", main: EVENT.venue, sub: EVENT.venueSub },
              { icon: Users, label: "Who", main: EVENT.teams, sub: EVENT.teamsSub },
            ].map((s) => (
              <div key={s.label} className="py-8 px-2 md:px-8">
                <div className="flex items-center gap-2 text-rust"><s.icon size={16} /><span className="label-font text-xs">{s.label}</span></div>
                <div className="mt-3 font-serif font-bold text-2xl">{s.main}</div>
                <div className="text-sm text-cream/70 mt-1">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / FOUNDATION */}
      <section className="paper-noise bg-background">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10">
            <SectionLabel>Est. 2024</SectionLabel>
            <h2 className="mt-5 font-serif font-black text-4xl sm:text-5xl text-espresso leading-tight">
              The Wayne Walker Foundation
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
              Rooted in the outdoors and the values of service, the Wayne Walker Foundation carries forward a legacy of generosity across Montgomery County, Texas. From the duck blind to the fairway, we bring our community together to raise meaningful support for the causes that protect and strengthen it.
            </p>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
              Our inaugural Heroes & Legacy Golf Classic channels that spirit into a single day of camaraderie, competition, and giving — with every dollar advancing the mission of our local first responders.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
              <Link to="/about" data-testid="home-about-link" className="inline-flex items-center gap-2 label-font text-xs text-rust border-b-2 border-rust pb-1 hover:gap-3 transition-all">
                More about the foundation <ArrowRight size={15} />
              </Link>
              <FoundationLink testId="home-foundation-site-link" className="label-font text-xs text-espresso/70 hover:text-rust transition-colors pb-1">
                Visit waynewalkerfoundation.org
              </FoundationLink>
            </div>
          </div>
          <div className="lg:col-span-5 relative z-10">
            <div className="border border-border bg-card hard-shadow overflow-hidden">
              <img src="/wayne-walker-tribute.jpg" alt="Wayne Walker tribute — portrait beside The Woodlands firefighter turnout gear" className="w-full h-80 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* THE CAUSE */}
      <section className="relative bg-espresso text-cream overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1 border border-cream/20 hard-shadow-rust overflow-hidden">
            <img src={IMAGES.firefighterPortrait} alt="First responder" className="w-full h-96 object-cover" />
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <SectionLabel>The Cause</SectionLabel>
            <h2 className="mt-5 font-serif font-black text-4xl sm:text-5xl leading-tight">
              Supporting Our <span className="text-rust">First Responders</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-cream/80 leading-relaxed">
              Every dollar from the Classic benefits the men and women who run toward danger. Proceeds provide meaningful support — financial assistance, cancer and behavioral-health support, and scholarships — for the firefighters, law-enforcement officers, EMS, and dispatchers who protect our community and answer the call every day.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {[
                { icon: Coins, label: "Financial Assistance" },
                { icon: HeartPulse, label: "Cancer & Health Support" },
                { icon: GraduationCap, label: "Scholarships" },
              ].map((c) => (
                <div key={c.label} className="border border-cream/20 p-5 bg-espresso/40">
                  <c.icon className="text-rust" size={22} />
                  <div className="mt-3 label-font text-xs text-cream/90">{c.label}</div>
                </div>
              ))}
            </div>
            <Link to="/cause" data-testid="home-cause-link" className="mt-8 inline-flex items-center gap-2 label-font text-xs text-rust border-b-2 border-rust pb-1 hover:gap-3 transition-all">
              Learn about the mission <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* THE DAY INCLUDES */}
      <section className="paper-noise bg-background">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32 relative z-10">
          <div className="max-w-2xl">
            <SectionLabel>The Experience</SectionLabel>
            <h2 className="mt-5 font-serif font-black text-4xl sm:text-5xl text-espresso">The Day Includes</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {DAY_INCLUDES.map((item, i) => (
              <motion.div
                key={item}
                initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="flex items-start gap-4 border border-border bg-card p-6 transition-transform hover:-translate-y-1 hard-shadow-sm"
              >
                <span className="shrink-0 mt-0.5 h-7 w-7 bg-rust text-primary-foreground flex items-center justify-center"><Check size={16} /></span>
                <span className="text-base text-espresso font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/event" data-testid="home-event-link" className="inline-flex items-center gap-2 label-font text-xs text-rust border-b-2 border-rust pb-1 hover:gap-3 transition-all">
              See the full schedule <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* SPONSOR PREVIEW */}
      <section className="bg-card paper-noise">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <SectionLabel>Sponsorship Tiers</SectionLabel>
              <h2 className="mt-5 font-serif font-black text-4xl sm:text-5xl text-espresso">Join The Mission</h2>
              <p className="mt-4 text-muted-foreground text-base">Put your brand behind those who serve. Five levels of partnership plus a Sponsor a Hero Team option — every one includes Founding Partner recognition.</p>
              <p data-testid="home-sponsor-deadlines" className="mt-3 text-sm text-rust font-medium">Founding Partner recognition through {SPONSOR_DEADLINES.foundingDate} · Commit by {SPONSOR_DEADLINES.commitDate} to guarantee signage, program listing & gift-bag inclusion.</p>
            </div>
            <Link to="/sponsor" className="label-font text-sm bg-rust text-primary-foreground px-7 py-3.5 border border-espresso transition-transform hover:-translate-y-1 hard-shadow whitespace-nowrap">All Tiers</Link>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...SPONSOR_TIERS, TRIBUTE_TIER].map((t, i) => (
              <motion.div
                key={t.key}
                data-testid={`home-tier-${t.key}`}
                initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className={`relative border p-7 transition-transform hover:-translate-y-1 ${t.highlight ? "border-rust bg-espresso text-cream hard-shadow-rust" : t.featured ? "border-2 border-rust bg-rust/10 text-espresso hard-shadow-sm" : "border-border bg-background text-espresso hard-shadow-sm"}`}
              >
                {t.featured && (
                  <span data-testid={`home-tier-badge-${t.key}`} className="absolute -top-3 left-5 label-font text-[10px] tracking-[0.15em] bg-rust text-primary-foreground px-3 py-1 border border-espresso">{t.badge}</span>
                )}
                <div className="flex items-center justify-between gap-2">
                  <div className="label-font text-xs text-rust">{t.name}</div>
                  <SpotsBadge remaining={remainingFor(cfg, t.key)} dark={!!t.highlight} testId={`home-spots-${t.key}`} />
                </div>
                <div className={`mt-3 font-serif font-black text-3xl ${SPONSOR_DETAILS_VISIBLE ? "" : "blur-[6px] select-none"}`}>${t.price.toLocaleString()}</div>
                {t.key === "tribute" && <p className="mt-2 text-xs text-muted-foreground leading-snug">Send a four-person first responder team to the course — with a team photo op for your company.</p>}
                <Link to="/sponsor" className={`mt-5 inline-flex items-center gap-2 label-font text-xs border-b-2 pb-1 transition-all hover:gap-3 ${t.highlight ? "text-cream border-cream" : t.featured ? "text-rust border-rust" : "text-espresso border-espresso"}`}>
                  Details <ArrowRight size={14} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-espresso text-cream overflow-hidden">
        <img src={IMAGES.texasSunset} alt="Texas golf course sunset" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-espresso to-espresso/50" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-28 text-center">
          <SectionLabel className="justify-center">Become a Sponsor · Register a Team · Join the Mission</SectionLabel>
          <h2 className="mt-6 font-serif font-black text-4xl sm:text-5xl lg:text-6xl">Tee It Up For A Cause</h2>
          <p className="mt-5 max-w-xl mx-auto text-cream/80 text-base md:text-lg">Spots are limited to {EVENT.teams.toLowerCase()}. Reserve yours today.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/register" data-testid="cta-register-btn" className="label-font text-sm bg-rust text-primary-foreground px-8 py-4 border border-cream/20 transition-transform hover:-translate-y-1 hard-shadow">Register Now</Link>
            <Link to="/sponsor" data-testid="cta-sponsor-btn" className="label-font text-sm border border-cream/40 px-8 py-4 transition-colors hover:bg-cream/10">Sponsor The Classic</Link>
            <Link to="/donate" data-testid="cta-donate-btn" className="label-font text-sm border border-cream/40 px-8 py-4 transition-colors hover:bg-cream/10">Donate</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
